<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <SellProductFiltersComponent
                    ref="sell-product-search"
                    :showShops="true"
                    :enableFieldType="false"
                    :showSingleArticleFilter="false"
                    preselectFamily
                    @searchPressed="getComprehensiveAnalysis"
                    @changeFieldType="
                        (f) => {
                            fieldType = f;
                        }
                    "
                ></SellProductFiltersComponent>
            </v-col>
        </v-row>

        <!-- Summary Cards Row 1 -->
        <v-row dense v-if="!loading && summary">
            <v-col cols="12" sm="4" md="4" lg="4">
                <v-card height="180">
                    <v-card-title class="text-subtitle-1 pa-2">
                        Analisi ABC
                    </v-card-title>
                    <v-card-text class="pa-2">
                        <div v-for="(stats, classType) in summary.abc_distribution" :key="classType" class="mb-1">
                            <small><strong>{{ classType }}:</strong> {{ stats.count }} ({{ stats.percentage.toFixed(1) }}%)</small>
                            <br>
                            <small>{{ stats.total_revenue | formatEuro }}</small>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="4" md="4" lg="4">
                <v-card height="180">
                    <v-card-title class="text-subtitle-1 pa-2">
                        Analisi XYZ
                    </v-card-title>
                    <v-card-text class="pa-2">
                        <div v-for="(stats, classType) in summary.xyz_distribution" :key="classType" class="mb-1">
                            <small><strong>{{ classType }}:</strong> {{ stats.count }} ({{ stats.percentage.toFixed(1) }}%)</small>
                            <br>
                            <small>Vel: {{ stats.avg_velocity | roundSecondDecimal }}</small>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="4" md="4" lg="4">
                <v-card height="180">
                    <v-card-title class="text-subtitle-1 pa-2">
                        Analisi FSN
                    </v-card-title>
                    <v-card-text class="pa-2">
                        <div v-for="(stats, classType) in summary.fsn_distribution" :key="classType" class="mb-1">
                            <small><strong>{{ classType }}:</strong> {{ stats.count }} ({{ stats.percentage.toFixed(1) }}%)</small>
                            <br>
                            <small>{{ stats.total_units_sold }} pz</small>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Summary Cards Row 2 -->
        <v-row dense v-if="!loading && summary">
            <v-col cols="12" sm="4" md="4" lg="4">
                <v-card height="180">
                    <v-card-title class="text-subtitle-1 pa-2">
                        Analisi EBITDA
                    </v-card-title>
                    <v-card-text class="pa-2">
                        <div v-for="(stats, classType) in summary.ebitda_distribution" :key="classType" class="mb-1">
                            <small><strong>{{ classType }}:</strong> {{ stats.count }} ({{ stats.percentage.toFixed(1) }}%)</small>
                            <br>
                            <small>{{ stats.total_margin | formatEuro }}</small>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="4" md="4" lg="4">
                <v-card height="180">
                    <v-card-title class="text-subtitle-1 pa-2">
                        Analisi RESA
                    </v-card-title>
                    <v-card-text class="pa-2">
                        <div v-for="(stats, classType) in summary.resa_distribution" :key="classType" class="mb-1">
                            <small><strong>{{ classType }}:</strong> {{ stats.count }} ({{ stats.percentage.toFixed(1) }}%)</small>
                            <br>
                            <small>{{ stats.total_space | roundSecondDecimal }} cm²</small>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="4" md="4" lg="4">
                <v-card height="180">
                    <v-card-title class="text-subtitle-1 pa-2">
                        Riepilogo
                    </v-card-title>
                    <v-card-text class="pa-2">
                        <div class="mb-1"><small><strong>Prodotti:</strong> {{ summary.total_products }}</small></div>
                        <div class="mb-1"><small><strong>Fatturato:</strong> {{ summary.total_revenue | formatEuro }}</small></div>
                        <div class="mb-1"><small><strong>Margine:</strong> {{ summary.total_margin | formatEuro }}</small></div>
                        <div class="mb-1"><small><strong>RESA:</strong> {{ summary.avg_euro_cm2 | formatEuro }}/cm²</small></div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>


        <!-- ABC/XYZ Matrix -->
        <v-row dense v-if="!loading && matrix">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="text-h6">
                        Matrice ABC/XYZ
                    </v-card-title>
                    <v-card-text>
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th class="text-center">X (Stabile)</th>
                                    <th class="text-center">Y (Variabile)</th>
                                    <th class="text-center">Z (Imprevedibile)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>A (Alto Valore)</strong></td>
                                    <td class="text-center">
                                        <v-chip small color="success" dark>{{ matrix.AX?.count || 0 }}</v-chip>
                                    </td>
                                    <td class="text-center">
                                        <v-chip small color="warning" dark>{{ matrix.AY?.count || 0 }}</v-chip>
                                    </td>
                                    <td class="text-center">
                                        <v-chip small color="error" dark>{{ matrix.AZ?.count || 0 }}</v-chip>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>B (Medio Valore)</strong></td>
                                    <td class="text-center">
                                        <v-chip small color="info" dark>{{ matrix.BX?.count || 0 }}</v-chip>
                                    </td>
                                    <td class="text-center">
                                        <v-chip small color="warning" dark>{{ matrix.BY?.count || 0 }}</v-chip>
                                    </td>
                                    <td class="text-center">
                                        <v-chip small color="error" dark>{{ matrix.BZ?.count || 0 }}</v-chip>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>C (Basso Valore)</strong></td>
                                    <td class="text-center">
                                        <v-chip small color="grey" dark>{{ matrix.CX?.count || 0 }}</v-chip>
                                    </td>
                                    <td class="text-center">
                                        <v-chip small color="grey" dark>{{ matrix.CY?.count || 0 }}</v-chip>
                                    </td>
                                    <td class="text-center">
                                        <v-chip small color="grey" dark>{{ matrix.CZ?.count || 0 }}</v-chip>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row dense>
            <v-col>
                <div v-if="!loading">
                    <v-data-table
                        dense
                        multi-sort
                        fixed-header
                        :search="search"
                        :item-class="rowColorClass"
                        :height="600"
                        :headers="headers"
                        :items="filteredData"
                        :options="{
                            sortBy: ['comprehensive_classification'],
                        }"
                        :items-per-page="100"
                        :footer-props="{
                            'items-per-page-options': [20, 50, 100, 200, -1],
                        }"
                    >
                        <template v-slot:top>
                            <v-toolbar flat>
                                <v-text-field
                                    dense
                                    v-model="search"
                                    label="Cerca..."
                                    class="mx-2"
                                    style="max-width: 200px;"
                                >
                                </v-text-field>
                                <v-select
                                    dense
                                    v-model="abcFilter"
                                    :items="abcFilterOptions"
                                    label="Filtro ABC"
                                    class="mx-2"
                                    style="max-width: 120px;"
                                    @change="applyFilters"
                                >
                                </v-select>
                                <v-select
                                    dense
                                    v-model="xyzFilter"
                                    :items="xyzFilterOptions"
                                    label="Filtro XYZ"
                                    class="mx-2"
                                    style="max-width: 120px;"
                                    @change="applyFilters"
                                >
                                </v-select>
                                <v-select
                                    dense
                                    v-model="fsnFilter"
                                    :items="fsnFilterOptions"
                                    label="Filtro FSN"
                                    class="mx-2"
                                    style="max-width: 120px;"
                                    @change="applyFilters"
                                >
                                </v-select>
                                <v-select
                                    dense
                                    v-model="ebitdaFilter"
                                    :items="ebitdaFilterOptions"
                                    label="Filtro EBITDA"
                                    class="mx-2"
                                    style="max-width: 120px;"
                                    @change="applyFilters"
                                >
                                </v-select>
                                <v-select
                                    dense
                                    v-model="resaFilter"
                                    :items="resaFilterOptions"
                                    label="Filtro RESA"
                                    class="mx-2"
                                    style="max-width: 120px;"
                                    @change="applyFilters"
                                >
                                </v-select>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="success"
                                    small
                                    dark
                                    class="mb-2"
                                    @click="exportComprehensiveAnalysis()"
                                >
                                    Esporta Analisi
                                    <v-icon small> assessment </v-icon>
                                </v-btn>
                            </v-toolbar>
                        </template>
                        <template v-slot:item.cod_articolo="{ item }">
                            <router-link
                                target="_blank"
                                :to="{
                                    name: 'product',
                                    query: {
                                        id: item.cod_articolo,
                                    },
                                }"
                            >
                                {{ item.cod_articolo }}
                            </router-link>
                        </template>
                        <template v-slot:item.abc_class="{ item }">
                            <v-chip small :color="getABCColor(item.abc_class)" dark>
                                {{ item.abc_class }}
                            </v-chip>
                        </template>
                        <template v-slot:item.xyz_class="{ item }">
                            <v-chip small :color="getXYZColor(item.xyz_class)" dark>
                                {{ item.xyz_class }}
                            </v-chip>
                        </template>
                        <template v-slot:item.fsn_class="{ item }">
                            <v-chip small :color="getFSNColor(item.fsn_class)" dark>
                                {{ item.fsn_class }}
                            </v-chip>
                        </template>
                        <template v-slot:item.ebitda_class="{ item }">
                            <v-chip small :color="getEBITDAColor(item.ebitda_class)" dark>
                                {{ item.ebitda_class }}
                            </v-chip>
                        </template>
                        <template v-slot:item.resa_class="{ item }">
                            <v-chip small :color="getRESAColor(item.resa_class)" dark>
                                {{ item.resa_class }}
                            </v-chip>
                        </template>
                        <template v-slot:item.comprehensive_classification="{ item }">
                            <v-chip small color="primary" dark>
                                {{ item.comprehensive_classification }}
                            </v-chip>
                        </template>
                        <template v-slot:item.prezzo_vendita="{ item }">
                            {{ item.prezzo_vendita | formatEuro }}
                        </template>
                        <template v-slot:item.prezzo_acquisto="{ item }">
                            {{ item.prezzo_acquisto | formatEuro }}
                        </template>
                        <template v-slot:item.prezzo_netto="{ item }">
                            {{ item.prezzo_netto | formatEuro }}
                        </template>
                        <template v-slot:item.prezzo_netto_tot="{ item }">
                            {{ item.prezzo_netto_tot | formatEuro }}
                        </template>
                        <template v-slot:item.rotazione="{ item }">
                            {{ item.rotazione | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.saturazione="{ item }">
                            {{ item.saturazione | formatPercent }}
                        </template>
                        <template v-slot:item.margine_unitario="{ item }">
                            {{ item.margine_unitario | formatEuro }}
                        </template>
                        <template v-slot:item.margine_totale="{ item }">
                            {{ item.margine_totale | formatEuro }}
                        </template>
                        <template v-slot:item.vendite_totali="{ item }">
                            {{ item.vendite_totali | formatEuro }}
                        </template>
                        <template v-slot:item.spazio="{ item }">
                            {{ item.spazio | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.euro_cm2="{ item }">
                            {{ item.euro_cm2 | formatEuro }}
                        </template>
                        <template v-slot:item.limit_vendita="{ item }">
                            {{ item.limit_vendita | formatEuro }}
                        </template>
                        <template v-slot:item.velocita="{ item }">
                            {{ item.velocita | roundSecondDecimal }}
                        </template>
                        <template v-slot:item.perla_nascosta="{ item }">
                            <div class="text-center">
                                <span
                                    v-if="item.perla_nascosta"
                                    style="color: green"
                                    >SI</span
                                ><span v-else style="color: red">NO</span>
                            </div>
                        </template>
                        <template v-slot:item.cumulata_margine="{ item }">
                            {{ item.cumulata_margine | formatPercent }}
                        </template>
                    </v-data-table>
                </div>
                <div v-else style="text-align: center">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                    ></v-progress-circular>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped lang="scss"></style>

<script>
import requests from "../../requests";
import SellProductFiltersComponent from "../../components/SellProductFiltersComponent.vue";
import $ from "jquery";

const headers = [
    { text: "Codice Articolo", value: "cod_articolo" },
    { text: "Descrizione", value: "ar_descr", width: 250 },
    { text: "ABC", value: "abc_class", align: "center", filterable: false },
    { text: "XYZ", value: "xyz_class", align: "center", filterable: false },
    { text: "FSN", value: "fsn_class", align: "center", filterable: false },
    { text: "EBITDA", value: "ebitda_class", align: "center", filterable: false },
    { text: "RESA", value: "resa_class", align: "center", filterable: false },
    { text: "Classificazione Completa", value: "comprehensive_classification", align: "center", filterable: false },

    { text: "Quantità", value: "qty_veduta", align: "end", filterable: false },
    {
        text: "Gicenza",
        value: "giacenza",
        align: "end",
        filterable: false,
    },
    {
        text: "Giacenza Media",
        value: "giacenza_media",
        align: "end",
        filterable: false,
    },
    {
        text: "Rotazione",
        value: "rotazione",
        align: "end",
        filterable: false,
    },

    { text: "Ultima Vendita", value: "data_ultima_vendita", filterable: false },
    { text: "Famiglia", value: "famiglia", width: 200 },
    { text: "Sottofamiglia", value: "sottofamiglia", width: 200 },

    { text: "H(cm)", value: "altezza", align: "end" },
    { text: "W(cm)", value: "larghezza", align: "end" },
    { text: "P(cm)", value: "profondita", align: "end" },

    { text: "Codice Fornitore", value: "cod_fornitore" },
    {
        text: "Prezzo Vendita",
        value: "prezzo_vendita",
        align: "end",
        filterable: false,
    },
    {
        text: "Prezzo Acquisto",
        value: "prezzo_acquisto",
        align: "end",
        filterable: false,
    },
    {
        text: "Margine Netto",
        value: "prezzo_netto",
        align: "end",
        filterable: false,
    },
    {
        text: "Tot Ven prz acq.",
        value: "prezzo_netto_tot",
        align: "end",
        filterable: false,
    },

    {
        text: "Totale Acquisti",
        value: "totale_acquisti",
        align: "end",
        filterable: false,
    },
    {
        text: "Data Primo Acquisto",
        value: "data_primo_acquisto",
        filterable: false,
    },

    {
        text: "Saturazione",
        value: "saturazione",
        align: "end",
        filterable: false,
    },
    {
        text: "Margine Totale",
        value: "margine_totale",
        align: "end",
        filterable: false,
    },

    {
        text: "Ven. Tot. ls2",
        value: "vendite_totali",
        align: "end",
        filterable: false,
    },
    { text: "Spazio(cm²)", value: "spazio", align: "end", filterable: false },
    { text: "Resa al cm²", value: "euro_cm2", align: "end", filterable: false },
    {
        text: "Fatturato target EBITDA",
        value: "limit_vendita",
        align: "end",
        filterable: false,
    },
    {
        text: "Giorni Attivi",
        value: "giorni_attivi",
        align: "end",
        filterable: false,
    },
    {
        text: "Velocità(pz/gg)",
        value: "velocita",
        align: "end",
        filterable: false,
    },

    {
        text: "Perla Nascosta",
        value: "perla_nascosta",
        align: "center",
        filterable: false,
    },
    {
        text: "Cumulata Margine",
        value: "cumulata_margine",
        align: "end",
        filterable: false,
    },
    { text: "Pareto", value: "pareto", filterable: false, width: 125 },
    { text: "Rating", value: "rating", align: "center", filterable: false },
];

export default {
    name: "product-comprehensive-analysis",
    components: { SellProductFiltersComponent },
    computed: {
        headers() {
            return headers;
        },
    },
    data: () => {
        return {
            loading: false,
            data: [],
            search: "",
            fieldType: "prezzo",
            abcFilter: "all",
            xyzFilter: "all",
            fsnFilter: "all",
            ebitdaFilter: "all",
            resaFilter: "all",
            abcFilterOptions: [
                { text: "Tutti", value: "all" },
                { text: "A", value: "A" },
                { text: "B", value: "B" },
                { text: "C", value: "C" }
            ],
            xyzFilterOptions: [
                { text: "Tutti", value: "all" },
                { text: "X", value: "X" },
                { text: "Y", value: "Y" },
                { text: "Z", value: "Z" }
            ],
            fsnFilterOptions: [
                { text: "Tutti", value: "all" },
                { text: "F (Fast)", value: "F" },
                { text: "S (Slow)", value: "S" },
                { text: "N (Non-moving)", value: "N" }
            ],
            ebitdaFilterOptions: [
                { text: "Tutti", value: "all" },
                { text: "A (Alto)", value: "A" },
                { text: "B (Medio)", value: "B" },
                { text: "C (Basso)", value: "C" }
            ],
            resaFilterOptions: [
                { text: "Tutti", value: "all" },
                { text: "A (Alto)", value: "A" },
                { text: "B (Medio)", value: "B" },
                { text: "C (Basso)", value: "C" }
            ],
            filteredData: [],
            summary: null,
            matrix: null,
        };
    },

    mounted() {
        console.log("Product Comprehensive Analysis Mounted.");
        let filters = {};
        this.getComprehensiveAnalysis(filters);
    },

    methods: {
        getComprehensiveAnalysis(filters) {
            console.log(filters);
            this.loading = true;
            requests
                .getComprehensiveAnalysisExtended(filters)
                .then((res) => {
                    this.data = res.data.products || [];
                    this.summary = res.data.summary || null;
                    this.matrix = res.data.matrix || null;
                    this.applyFilters();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        rowColorClass(row) {
            // Color based on comprehensive classification
            const classification = row.comprehensive_classification;
            if (classification.includes('A') && classification.includes('X') && classification.includes('F')) {
                return "row-color-excellent"; // AXF - Excellent
            } else if (classification.includes('A') && classification.includes('F')) {
                return "row-color-very-good"; // AF combinations - Very Good
            } else if (classification.includes('N')) {
                return "row-color-poor"; // Any N - Poor
            } else if (classification.includes('C') && classification.includes('Z')) {
                return "row-color-bad"; // CZ combinations - Bad
            }
            return "";
        },
        getABCColor(abcClass) {
            switch (abcClass) {
                case 'A': return 'success';
                case 'B': return 'warning';
                case 'C': return 'error';
                default: return 'grey';
            }
        },
        getXYZColor(xyzClass) {
            switch (xyzClass) {
                case 'X': return 'success';
                case 'Y': return 'warning';
                case 'Z': return 'error';
                default: return 'grey';
            }
        },
        getFSNColor(fsnClass) {
            switch (fsnClass) {
                case 'F': return 'success';
                case 'S': return 'warning';
                case 'N': return 'error';
                default: return 'grey';
            }
        },
        getEBITDAColor(ebitdaClass) {
            switch (ebitdaClass) {
                case 'A': return 'success';
                case 'B': return 'warning';
                case 'C': return 'error';
                default: return 'grey';
            }
        },
        getRESAColor(resaClass) {
            switch (resaClass) {
                case 'A': return 'success';
                case 'B': return 'warning';
                case 'C': return 'error';
                default: return 'grey';
            }
        },
        getEfficientProducts(type) {
            if (!this.summary) return 0;
            const distribution = type === 'ebitda' ? this.summary.ebitda_distribution : this.summary.resa_distribution;
            return distribution && distribution.A ? distribution.A.count : 0;
        },
        exportComprehensiveAnalysis() {
            let filters = this.$refs["sell-product-search"].getFilters();
            if (!filters.shop_id) {
                filters.shop_id = 7;
            }
            window.open(
                "/api/products/comprehensive-analysis?" + $.param(filters),
                "_blank"
            );
        },
        applyFilters() {
            let filtered = this.data;

            // Apply ABC filter
            if (this.abcFilter !== "all") {
                filtered = filtered.filter(item => item.abc_class === this.abcFilter);
            }

            // Apply XYZ filter
            if (this.xyzFilter !== "all") {
                filtered = filtered.filter(item => item.xyz_class === this.xyzFilter);
            }

            // Apply FSN filter
            if (this.fsnFilter !== "all") {
                filtered = filtered.filter(item => item.fsn_class === this.fsnFilter);
            }

            // Apply EBITDA filter
            if (this.ebitdaFilter !== "all") {
                filtered = filtered.filter(item => item.ebitda_class === this.ebitdaFilter);
            }

            // Apply RESA filter
            if (this.resaFilter !== "all") {
                filtered = filtered.filter(item => item.resa_class === this.resaFilter);
            }

            this.filteredData = filtered;
        }
    },

    filters: {
        roundSecondDecimal(value) {
            return Math.round(value * 100) / 100;
        },

        formatEuro(number) {
            if (_.isNaN(number)) return 0;
            return new Intl.NumberFormat("it-IT", {
                style: "currency",
                currency: "EUR",
            }).format(number);
        },
        formatPercent(value) {
            return value.toFixed(2) + "%";
        },
    },
};
</script>

<style lang="scss">
.row-color-excellent {
    background-color: rgba($color: #4CAF50, $alpha: 0.3) !important;
}
.row-color-very-good {
    background-color: rgba($color: #8BC34A, $alpha: 0.3) !important;
}
.row-color-poor {
    background-color: rgba($color: #FF9800, $alpha: 0.3) !important;
}
.row-color-bad {
    background-color: rgba($color: #F44336, $alpha: 0.3) !important;
}
</style>
